import { useEffect, useRef } from "react";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { useNavigate } from "react-router-dom";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { isBankIdResumeUrl, parseExternalUrlToAppRoute } from "@/lib/native/parseAppUrl";
import { closeBankIdSystemBrowser } from "@/lib/native/bankIdBrowser";
import {
  peekPendingBankIdReturn,
  stashPendingBankIdReturn,
  takePendingBankIdReturn,
} from "@/lib/native/pendingBankIdReturn";

export {
  stashPendingBankIdReturn,
  takePendingBankIdReturn,
  peekPendingBankIdReturn,
} from "@/lib/native/pendingBankIdReturn";

/** BankID / contract returns must replace history so Back does not replay nativeCode. */
function shouldReplaceRoute(route: string): boolean {
  const qIndex = route.indexOf("?");
  if (qIndex < 0) return false;
  const params = new URLSearchParams(route.slice(qIndex + 1));
  return (
    params.has("nativeCode") ||
    params.has("bankid") ||
    params.has("bankidError") ||
    params.has("bankidRegister") ||
    params.has("bankidRegisterError") ||
    params.has("contractSignatureStatus")
  );
}

function navigateToAppRoute(
  route: string,
  navigate: ReturnType<typeof useNavigate>,
) {
  const qIndex = route.indexOf("?");
  const pathPart = qIndex >= 0 ? route.slice(0, qIndex) : route;
  const queryPart = qIndex >= 0 ? route.slice(qIndex + 1) : "";
  const path = pathPart.startsWith("/") ? pathPart : `/${pathPart}`;
  const search = queryPart ? `?${queryPart}` : "";
  const replace = shouldReplaceRoute(route);
  if (path.startsWith("/auth") && search && shouldReplaceRoute(`${path}${search}`)) {
    stashPendingBankIdReturn(search);
  }
  // HashRouter updates the hash via navigate — do not also set window.location.hash
  // (that pushed a second history entry and made Back replay BankID returns).
  navigate({ pathname: path, search }, { replace });
}

/** Handle cold start + warm deep links into listing routes / BankID return etc. */
export function useNativeDeepLinks() {
  const navigate = useNavigate();
  const { isNative } = useNativePlatform();
  const lastHandledUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!isNative) return;

    const handleUrl = (rawUrl: string) => {
      if (!rawUrl || lastHandledUrl.current === rawUrl) return;
      if (isBankIdResumeUrl(rawUrl)) {
        console.info("[deepLink] BankID resume – letting auth session finish");
        return;
      }
      const route = parseExternalUrlToAppRoute(rawUrl);
      if (!route) return;
      lastHandledUrl.current = rawUrl;
      console.info("[deepLink] open", rawUrl, "→", route);
      void closeBankIdSystemBrowser();
      navigateToAppRoute(route, navigate);
    };

    const applyPendingIfNeeded = () => {
      const pending = peekPendingBankIdReturn();
      if (!pending) return;
      const params = new URLSearchParams(
        pending.startsWith("?") ? pending.slice(1) : pending,
      );
      if (
        !params.has("nativeCode") &&
        !params.has("bankidError") &&
        !params.has("bankidRegisterError") &&
        !params.has("bankidRegister") &&
        !params.has("bankid")
      ) {
        return;
      }
      const taken = takePendingBankIdReturn() || pending;
      console.info("[deepLink] applying pending BankID return", taken);
      // Already consumed from stash — navigate without re-stashing.
      const search = taken.startsWith("?") ? taken : `?${taken}`;
      navigate({ pathname: "/auth", search }, { replace: true });
    };

    void App.getLaunchUrl().then((result) => {
      if (result?.url) handleUrl(result.url);
      else applyPendingIfNeeded();
    });

    const subPromise = App.addListener("appUrlOpen", (event) => {
      if (event.url) handleUrl(event.url);
    });

    const statePromise = App.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) return;
      // Custom Tabs sometimes resumes the app without a fresh appUrlOpen;
      // re-check launch URL + stashed pending return.
      void App.getLaunchUrl()
        .then((result) => {
          if (result?.url) handleUrl(result.url);
          else applyPendingIfNeeded();
        })
        .catch(() => applyPendingIfNeeded());
    });

    let browserFinishedPromise: Promise<{ remove: () => Promise<void> }> | null =
      null;
    if (Capacitor.getPlatform() === "android") {
      browserFinishedPromise = Browser.addListener("browserFinished", () => {
        console.info("[deepLink] Custom Tabs finished – checking BankID return");
        void App.getLaunchUrl()
          .then((result) => {
            if (result?.url) handleUrl(result.url);
            else applyPendingIfNeeded();
          })
          .catch(() => applyPendingIfNeeded());
      });
    }

    return () => {
      void subPromise.then((sub) => sub.remove());
      void statePromise.then((sub) => sub.remove());
      if (browserFinishedPromise) {
        void browserFinishedPromise.then((sub) => sub.remove());
      }
    };
  }, [navigate, isNative]);
}
